import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { queryClient } from '../queryClient';
import { 
  getActiveJob, 
  clearActiveJob, 
  saveActiveJob,
  listenAiHeavyJobStream, 
  startAiHeavyJob,
  normalizeGeneratedQuestions,
  AiJobStatusEvent, 
  AiHeavyJobStored, 
  AiHeavyJobPayload
} from '../services/aiHeavyJobService';
import { getFriendlyErrorMessage } from '../utils/aiErrorUtils';
import { customFetch } from '../services/apiService';
import { QuestionSet } from '../types';

export interface GeneratedQuestionItem {
  id?: string;
  questionText: string;
  questionMedia?: string;
  options: Array<{
    id?: string;
    key?: string;
    text: string;
    isCorrect: boolean;
  }>;
  explanation?: string;
  difficulty?: string;
  sourceFileUrl?: string;
}

export type AiModalStep = 'input' | 'processing' | 'result';

interface AiHeavyJobContextType {
  currentStep: AiModalStep;
  setCurrentStep: (step: AiModalStep) => void;
  activeJob: AiHeavyJobStored | null;
  jobEvent: AiJobStatusEvent | null;
  isStartingJob: boolean;
  isProcessing: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  isModalOpen: boolean;
  isMinimized: boolean;
  completedQuestions: GeneratedQuestionItem[];
  setCompletedQuestions: React.Dispatch<React.SetStateAction<GeneratedQuestionItem[]>>;
  targetSet: QuestionSet | null;
  
  // Actions
  startJob: (payload: AiHeavyJobPayload, targetSetInfo?: QuestionSet | null) => Promise<string | null>;
  openModal: (setInfo?: QuestionSet | null) => void;
  closeModal: () => void;
  minimizeModal: () => void;
  maximizeModal: () => void;
  dismissJob: () => void;
  resetToInput: () => void;
  
  // Question review editing
  updateQuestionText: (index: number, text: string) => void;
  updateOptionText: (qIndex: number, optIndex: number, text: string) => void;
  selectCorrectOption: (qIndex: number, optIndex: number) => void;
  updateExplanation: (index: number, text: string) => void;
  updateDifficulty: (index: number, diff: string) => void;
  deleteQuestion: (index: number) => void;
  saveQuestionsToSet: (setId?: string, onComplete?: (count: number) => void) => Promise<boolean>;
}

const AiHeavyJobContext = createContext<AiHeavyJobContextType | undefined>(undefined);

// Web Audio API Gentle Sound Effect for Job Completion
const playCompletionChime = () => {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const now = ctx.currentTime;

    // First tone (E5 ~ 659.25Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(659.25, now);
    gain1.gain.setValueAtTime(0.12, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.4);

    // Second tone (A5 ~ 880Hz) - higher & brighter
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880, now + 0.12);
    gain2.gain.setValueAtTime(0.15, now + 0.12);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.12);
    osc2.stop(now + 0.6);
  } catch (e) {
    // AudioContext blocked by browser autoplay policy if user hasn't interacted
  }
};

export const AiHeavyJobProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState<AiModalStep>('input');
  const [activeJob, setActiveJob] = useState<AiHeavyJobStored | null>(null);
  const [jobEvent, setJobEvent] = useState<AiJobStatusEvent | null>(null);
  const [isStartingJob, setIsStartingJob] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isMinimized, setIsMinimized] = useState<boolean>(false);
  const [completedQuestions, setCompletedQuestions] = useState<GeneratedQuestionItem[]>([]);
  const [targetSet, setTargetSet] = useState<QuestionSet | null>(null);

  const cancelStreamRef = useRef<(() => void) | null>(null);

  const isProcessing = jobEvent?.status === 'processing' || isStartingJob;
  const isCompleted = jobEvent?.status === 'completed' || currentStep === 'result';
  const isFailed = jobEvent?.status === 'failed';

  // Yêu cầu Kỹ thuật 3: Prevent Unload (Cảnh báo chống tải lại trang khi đang chạy AI)
  useEffect(() => {
    const shouldPreventUnload = Boolean(activeJob && isProcessing);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const msg = 'Tiến trình AI đang chạy. Rời khỏi trang lúc này sẽ làm mất kết nối. Bạn có chắc chắn?';
      e.preventDefault();
      e.returnValue = msg;
      return msg;
    };

    if (shouldPreventUnload) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [activeJob, isProcessing]);

  // Phục hồi Job từ LocalStorage khi khởi động ứng dụng
  useEffect(() => {
    const saved = getActiveJob();
    if (saved?.jobId) {
      console.log('[AI Job Global Provider] Found active job in storage:', saved.jobId);
      setActiveJob(saved);
      setIsMinimized(true); // Default to minimized dock on cold refresh
      setCurrentStep('processing');
      startListening(saved.jobId, true);
    }

    return () => {
      if (cancelStreamRef.current) {
        cancelStreamRef.current();
        cancelStreamRef.current = null;
      }
    };
  }, []);

  const startListening = (jobId: string, fromRecovery: boolean = false) => {
    if (cancelStreamRef.current) {
      cancelStreamRef.current();
      cancelStreamRef.current = null;
    }

    setCurrentStep('processing');
    setJobEvent({
      jobId,
      status: 'processing',
      progress: fromRecovery ? 25 : 10,
      message: fromRecovery 
        ? 'Đang kết nối lại tiến trình chạy ngầm...' 
        : 'Gemini đang phân tích tài liệu và khởi tạo câu hỏi...'
    });

    const cancel = listenAiHeavyJobStream(
      jobId,
      (event: AiJobStatusEvent) => {
        setJobEvent(event);

        if (event.status === 'processing') {
          setCurrentStep('processing');
        }

        if (event.status === 'completed') {
          playCompletionChime();
          clearActiveJob();

          // Bước 2: Chuẩn hóa và bóc tách dữ liệu câu hỏi an toàn từ event / resultText
          try {
            const parsedQuestions = normalizeGeneratedQuestions(event);
            if (parsedQuestions && parsedQuestions.length > 0) {
              setCompletedQuestions(parsedQuestions);
              setCurrentStep('result');
            } else {
              throw new Error("AI không trả về đúng định dạng JSON.");
            }
          } catch (error) {
            console.error("Lỗi parse JSON từ AI:", error);
            toast.error("Không thể đọc dữ liệu từ AI. Vui lòng thử lại với prompt cụ thể hơn.", {
              id: `ai-job-${jobId}`,
              duration: 6000
            });
            setCurrentStep('input');
          }
        }

        if (event.status === 'failed') {
          const friendly = getFriendlyErrorMessage(event.errorMessage || event.errorCode);
          toast.error(friendly, { id: `ai-job-${jobId}`, duration: 6000 });
          clearActiveJob();
          setCurrentStep('input');
        }
      },
      (err: any) => {
        console.error('[SSE Error in AI Job Provider]:', err);
        const friendly = getFriendlyErrorMessage(err);
        toast.error(friendly, { id: `ai-sse-error`, duration: 5000 });
      }
    );

    cancelStreamRef.current = cancel;
  };

  const startJob = async (
    payload: AiHeavyJobPayload, 
    targetSetInfo?: QuestionSet | null
  ): Promise<string | null> => {
    setIsStartingJob(true);
    setCurrentStep('processing');
    if (targetSetInfo) {
      setTargetSet(targetSetInfo);
    }

    // Làm sạch và bóc tách dữ liệu nguyên thủy (Pure Primitives)
    const cleanPayload: AiHeavyJobPayload = {
      prompt: typeof payload?.prompt === 'string' ? payload.prompt.trim() : String(payload?.prompt || ''),
      fileUrls: Array.isArray(payload?.fileUrls) 
        ? payload.fileUrls.map(u => (typeof u === 'string' ? u.trim() : '')).filter(Boolean) 
        : [],
      fileUrl: typeof payload?.fileUrl === 'string' && payload.fileUrl.trim() ? payload.fileUrl.trim() : undefined,
      requestedModel: typeof payload?.requestedModel === 'string' && payload.requestedModel.trim() 
        ? payload.requestedModel.trim() 
        : 'gemini-2.5-flash',
      subject: typeof payload?.subject === 'string' ? payload.subject : String(payload?.subject || ''),
      grade: typeof payload?.grade === 'string' ? payload.grade : String(payload?.grade || ''),
      difficulty: typeof payload?.difficulty === 'string' ? payload.difficulty : 'Hỗn hợp',
      questionCount: typeof payload?.questionCount === 'number' ? payload.questionCount : (Number(payload?.questionCount) || 5),
      targetSetId: typeof payload?.targetSetId === 'string' ? payload.targetSetId : (payload?.targetSetId ? String(payload.targetSetId) : undefined)
    };

    try {
      const result = await startAiHeavyJob(cleanPayload);
      const jobId = result.jobId;

      const newRecord: AiHeavyJobStored = {
        jobId,
        timestamp: Date.now(),
        params: cleanPayload
      };

      saveActiveJob(jobId, cleanPayload);
      setActiveJob(newRecord);
      setCompletedQuestions([]);

      // Trạng thái 1: Modal Chờ (Khởi tạo) - giữ modal mở để user theo dõi hoặc bấm thu nhỏ
      setIsModalOpen(true);
      setIsMinimized(false);
      setCurrentStep('processing');

      startListening(jobId, false);
      return jobId;
    } catch (err: any) {
      console.error('[Start AI Heavy Job Failed]:', err);
      const friendly = getFriendlyErrorMessage(err);
      toast.error(friendly, { id: 'ai-start-err', duration: 5000 });
      setCurrentStep('input');
      throw err;
    } finally {
      setIsStartingJob(false);
    }
  };

  const openModal = (setInfo?: QuestionSet | null) => {
    if (setInfo) {
      setTargetSet(setInfo);
    }
    if (jobEvent?.status === 'completed' || completedQuestions.length > 0) {
      setCurrentStep('result');
    } else if (jobEvent?.status === 'processing' || isStartingJob) {
      setCurrentStep('processing');
    } else {
      setCurrentStep('input');
    }
    setIsModalOpen(true);
    setIsMinimized(false);
  };

  const closeModal = () => {
    // Nếu job đang chạy, tự động chuyển về dạng thu nhỏ để không bị gián đoạn
    if (isProcessing) {
      setIsMinimized(true);
      setIsModalOpen(false);
    } else {
      setIsModalOpen(false);
      setIsMinimized(false);
    }
  };

  const minimizeModal = () => {
    // Yêu cầu: Cập nhật isMinimized = true, Modal chính bị ẩn đi, Widget góc dưới hiện ra.
    // Không bắn Toast thông báo thừa gây rác màn hình.
    setIsMinimized(true);
    setIsModalOpen(false);
  };

  const maximizeModal = () => {
    // Yêu cầu: Cập nhật isMinimized = false, Widget góc biến mất, Modal chính mở lại (isModalOpen = true)
    if (jobEvent?.status === 'completed' || completedQuestions.length > 0) {
      setCurrentStep('result');
    } else if (jobEvent?.status === 'processing' || isStartingJob) {
      setCurrentStep('processing');
    } else {
      setCurrentStep('input');
    }
    setIsMinimized(false);
    setIsModalOpen(true);
  };

  const dismissJob = () => {
    if (cancelStreamRef.current) {
      cancelStreamRef.current();
      cancelStreamRef.current = null;
    }
    clearActiveJob();
    setActiveJob(null);
    setJobEvent(null);
    setCompletedQuestions([]);
    setCurrentStep('input');
    setIsModalOpen(false);
    setIsMinimized(false);
  };

  const resetToInput = () => {
    if (cancelStreamRef.current) {
      cancelStreamRef.current();
      cancelStreamRef.current = null;
    }
    clearActiveJob();
    setActiveJob(null);
    setJobEvent(null);
    setCompletedQuestions([]);
    setCurrentStep('input');
  };

  // Review editing methods
  const updateQuestionText = (index: number, text: string) => {
    setCompletedQuestions(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = { ...updated[index], questionText: text };
      }
      return updated;
    });
  };

  const updateOptionText = (qIndex: number, optIndex: number, text: string) => {
    setCompletedQuestions(prev => {
      const updated = [...prev];
      if (updated[qIndex] && updated[qIndex].options[optIndex]) {
        const newOpts = [...updated[qIndex].options];
        newOpts[optIndex] = { ...newOpts[optIndex], text };
        updated[qIndex] = { ...updated[qIndex], options: newOpts };
      }
      return updated;
    });
  };

  const selectCorrectOption = (qIndex: number, optIndex: number) => {
    setCompletedQuestions(prev => {
      const updated = [...prev];
      if (updated[qIndex] && updated[qIndex].options) {
        const newOpts = updated[qIndex].options.map((opt, idx) => ({
          ...opt,
          isCorrect: idx === optIndex
        }));
        updated[qIndex] = { ...updated[qIndex], options: newOpts };
      }
      return updated;
    });
  };

  const updateExplanation = (index: number, text: string) => {
    setCompletedQuestions(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = { ...updated[index], explanation: text };
      }
      return updated;
    });
  };

  const updateDifficulty = (index: number, diff: string) => {
    setCompletedQuestions(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = { ...updated[index], difficulty: diff };
      }
      return updated;
    });
  };

  const deleteQuestion = (index: number) => {
    setCompletedQuestions(prev => prev.filter((_, i) => i !== index));
  };

  const saveQuestionsToSet = async (
    setId?: string, 
    onComplete?: (count: number) => void
  ): Promise<boolean> => {
    const targetId = setId || targetSet?.id || targetSet?._id || activeJob?.params?.targetSetId;
    if (!targetId) {
      toast.error('Không tìm thấy thông tin bộ câu hỏi đích.');
      return false;
    }

    if (completedQuestions.length === 0) {
      toast.error('Không có câu hỏi nào để lưu.');
      return false;
    }

    try {
      const res = await customFetch('/api/v1/questions/bulk-add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          setId: String(targetId),
          questions: completedQuestions
        })
      });

      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        const addedCount = data.count || completedQuestions.length;
        toast.success(`Đã thêm thành công ${addedCount} câu hỏi vào bộ!`);
        
        try {
          queryClient.invalidateQueries({ queryKey: ['questionSetDetail'] });
          queryClient.invalidateQueries({ queryKey: ['myQuestionSets'] });
        } catch (e) {
          // ignore cache invalidate error
        }

        dismissJob();
        if (onComplete) {
          onComplete(addedCount);
        }
        return true;
      } else {
        throw new Error(data.message || 'Lỗi khi lưu câu hỏi vào hệ thống.');
      }
    } catch (err: any) {
      console.error('[Save Generated Questions Error]:', err);
      toast.error(err.message || 'Không thể lưu câu hỏi. Vui lòng thử lại.');
      return false;
    }
  };

  return (
    <AiHeavyJobContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        activeJob,
        jobEvent,
        isStartingJob,
        isProcessing,
        isCompleted,
        isFailed,
        isModalOpen,
        isMinimized,
        completedQuestions,
        setCompletedQuestions,
        targetSet,
        startJob,
        openModal,
        closeModal,
        minimizeModal,
        maximizeModal,
        dismissJob,
        resetToInput,
        updateQuestionText,
        updateOptionText,
        selectCorrectOption,
        updateExplanation,
        updateDifficulty,
        deleteQuestion,
        saveQuestionsToSet
      }}
    >
      {children}
    </AiHeavyJobContext.Provider>
  );
};

export const useAiHeavyJob = () => {
  const context = useContext(AiHeavyJobContext);
  if (!context) {
    throw new Error('useAiHeavyJob must be used within an AiHeavyJobProvider');
  }
  return context;
};
